// ════════════════════════════════════════════════════════════════════════
// cpcAlloc.js — derive the per-day CPC for a date from CPC allocations.
// ════════════════════════════════════════════════════════════════════════
// CPC (ad/campaign spend) is entered as a from–to date range + amount and
// spread EVENLY across every day in [dateFrom, dateTo] (inclusive). A range
// that crosses a calendar-month boundary therefore charges each month the CPC
// for its own days automatically — the month split falls out of the per-day
// day-count, no special-casing needed. VAT on CPC stays auto downstream
// (calcDaily computes vatOnCpc = perDayCPC × vatRate).
//
// Allocation shape (normalised): { channelId, dateFrom, dateTo, cpc }
//   dateFrom / dateTo : "YYYY-MM-DD" strings
//   cpc               : number (the WHOLE range amount, not per-day)
//
// MUST stay in lockstep with backend/lib/cpcAlloc.cjs (same math, used by the
// email/xlsx reports so on-screen and report numbers tie out).

// "YYYY-MM-DD" -> UTC ms (UTC avoids DST off-by-one in day-count math). NaN if invalid.
function ymdToUTC(s) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(s || ""))) return NaN;
  const [y, m, d] = s.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

// Inclusive day-count of [dateFrom, dateTo]. 0 if invalid or reversed.
export function dayCount(dateFrom, dateTo) {
  const a = ymdToUTC(dateFrom);
  const b = ymdToUTC(dateTo);
  if (isNaN(a) || isNaN(b) || b < a) return 0;
  return Math.round((b - a) / 86400000) + 1;
}

// Even per-day share of one allocation's amount across its inclusive day range.
export function perDayShare(alloc) {
  const n = dayCount(alloc && alloc.dateFrom, alloc && alloc.dateTo);
  if (n <= 0) return 0;
  return (Number(alloc.cpc) || 0) / n;
}

// Summed per-day CPC for a single date "YYYY-MM-DD". Allocations whose range
// covers `ymd` contribute their per-day share; overlapping allocations (e.g.
// two concurrent campaigns) are SUMMED. When `channelId` is given, only
// allocations for that channel count.
export function cpcForDate(allocations, ymd, channelId) {
  if (!allocations || !allocations.length || !ymd) return 0;
  let sum = 0;
  for (const a of allocations) {
    if (channelId != null && a.channelId !== channelId) continue;
    if (ymd >= a.dateFrom && ymd <= a.dateTo) sum += perDayShare(a);
  }
  return sum;
}

// Total CPC allocated to a PERIOD [fromYMD, toYMD] (inclusive) — the per-day
// share × the number of the range's days that fall in the window, summed over
// allocations. This counts EVERY day in the period the range covers, whether or
// not that day has a sales-log entry — so the full committed CPC comes off the
// P&L. Pass null/"" for fromYMD or toYMD to leave that side unbounded (e.g. an
// "all time" total = the full entered amounts). Optional channelId filter.
export function cpcForPeriod(allocations, fromYMD, toYMD, channelId) {
  if (!allocations || !allocations.length) return 0;
  let sum = 0;
  for (const a of allocations) {
    if (channelId != null && a.channelId !== channelId) continue;
    const total = dayCount(a.dateFrom, a.dateTo);
    if (total <= 0) continue;
    const from = fromYMD && fromYMD > a.dateFrom ? fromYMD : a.dateFrom;
    const to   = toYMD   && toYMD   < a.dateTo   ? toYMD   : a.dateTo;
    const overlap = dayCount(from, to); // 0 when the window misses the range
    if (overlap <= 0) continue;
    sum += (Number(a.cpc) || 0) * overlap / total;
  }
  return sum;
}
